/**
 * One-shot localStorage handoff for carrying a HAT console's captured lines
 * into the Machine Comm Analyser (`/machine-comm-analyser`) without touching
 * the live console session.
 *
 * Flow: the console page snapshots the capture text it currently has on
 * screen, stashes it under a fresh unique key via {@link stashConsoleCapture},
 * and `window.open`s the analyser route in a NEW TAB with that key on the
 * query string ({@link buildAnalyserHandoffUrl}). The analyser page, on
 * mount, reads the key off its own URL and calls {@link consumeConsoleCapture},
 * which returns the text and immediately deletes the entry — a second read
 * of the same key (a reload, a reopened stale link, a remount) returns
 * `null` rather than replaying the capture. The console tab/session is never
 * touched by any of this — it's a pure storage round-trip plus a new
 * browser tab.
 *
 * localStorage (NOT sessionStorage) is deliberate and load-bearing: per the
 * HTML/Storage spec, a brand-new tab opened via `window.open` only inherits
 * a clone of the opener's sessionStorage when an opener relationship
 * exists — and this handoff opens with `noopener,noreferrer` (legitimate
 * tabnabbing protection, kept), which severs that relationship. A
 * `noopener`-opened tab always starts with EMPTY sessionStorage regardless
 * of origin, so a sessionStorage-backed stash can never be read by the new
 * tab. localStorage is origin-scoped and shared across all same-origin tabs
 * regardless of opener relationship, so it's the only Web Storage mechanism
 * that actually crosses a `noopener` `window.open` boundary. The one-shot
 * (delete-on-read) semantics plus a short TTL (below) keep this from
 * behaving like a normal persistent store.
 */

const STORAGE_PREFIX = 'arms.sasConsoleHandoff.';

/** Query-string flag on the analyser route that carries the stash key. */
export const CONSOLE_HANDOFF_QUERY_PARAM = 'consoleCapture';

/** Analyser route path the handoff opens (no origin — relative for `window.open`). */
const ANALYSER_PATH = '/machine-comm-analyser';

/**
 * How long a stash is considered valid, from the moment it was stashed.
 * localStorage has no built-in expiry, so without a TTL an abandoned click
 * (tab never opened, or opened long after) would sit there indefinitely —
 * this bound both caps how "stale but still valid" a resurrected stash can
 * be and gives {@link purgeExpiredStashes} something concrete to sweep.
 * Generous relative to the actual click→new-tab→mount window (well under a
 * second in practice).
 */
const STASH_TTL_MS = 10 * 60 * 1000; // 10 minutes

type StashPayload = { text: string; stashedAt: number };

/** Collision-safe per-stash suffix. Not a crypto UUID — this only needs to
 * be unique within this origin's localStorage for the TTL window, not
 * globally or cryptographically. */
function uniqueSuffix(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function isStashKey(key: string): boolean {
  return key.startsWith(STORAGE_PREFIX);
}

/** Parses a raw localStorage value into a {@link StashPayload}, or `null`
 * for anything missing/malformed/shape-mismatched — never throws. */
function readPayload(raw: string | null): StashPayload | null {
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StashPayload>;
    if (typeof parsed.text !== 'string' || typeof parsed.stashedAt !== 'number') return null;
    return { text: parsed.text, stashedAt: parsed.stashedAt };
  } catch {
    return null;
  }
}

function isExpired(payload: StashPayload, now: number): boolean {
  return now - payload.stashedAt > STASH_TTL_MS;
}

/**
 * Opportunistic sweep of every `arms.sasConsoleHandoff.*` key past
 * {@link STASH_TTL_MS} (or unparsable). Runs once at the top of every
 * {@link stashConsoleCapture} call — no background timer, no cross-tab
 * coordination needed — so an abandoned click (the tab it opened was never
 * used, or the analyser was never opened at all) doesn't leave an orphaned
 * key in localStorage forever.
 */
function purgeExpiredStashes(now: number): void {
  const staleKeys: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key || !isStashKey(key)) continue;
    const payload = readPayload(window.localStorage.getItem(key));
    if (!payload || isExpired(payload, now)) staleKeys.push(key);
  }
  staleKeys.forEach((key) => window.localStorage.removeItem(key));
}

/**
 * Snapshot `text` into localStorage under a fresh one-shot key (timestamped
 * for the TTL check on read) and return that key. Every call gets its own
 * key — repeat clicks (or clicks from different consoles) each produce an
 * independent, non-colliding stash; an earlier stash that hasn't been
 * consumed yet (e.g. its tab hasn't finished loading) is never overwritten
 * or clobbered by a later click. Also opportunistically purges any
 * already-expired stashes left over from earlier abandoned clicks.
 */
export function stashConsoleCapture(text: string): string {
  const now = Date.now();
  purgeExpiredStashes(now);
  const key = `${STORAGE_PREFIX}${uniqueSuffix()}`;
  const payload: StashPayload = { text, stashedAt: now };
  window.localStorage.setItem(key, JSON.stringify(payload));
  return key;
}

/**
 * Read + immediately delete the stash at `key` (one-shot — the key is
 * removed whether or not it turns out to be valid). Returns `null` for a
 * missing key, an already-consumed key, a malformed entry, a key older than
 * {@link STASH_TTL_MS}, or a falsy `key` itself — callers use that to fall
 * back to a normal empty page with a visible notice rather than crashing or
 * silently replaying an old/stale capture.
 *
 * `key` comes straight off the URL's `?consoleCapture=` query param — i.e.
 * it's attacker-controlled input (a crafted link). Without the
 * {@link isStashKey} guard, a caller could name ANY same-origin localStorage
 * key (e.g. `"theme"`) and this function would read it, feed its value into
 * the analyser's paste box, and DELETE it — a same-origin key-deletion +
 * exfiltration primitive. Anything not shaped like one of our own
 * `arms.sasConsoleHandoff.*` stash keys is rejected untouched: no read, no
 * delete, no side effect whatsoever on storage outside this module's own
 * namespace.
 */
export function consumeConsoleCapture(key: string | null | undefined): string | null {
  if (!key || !isStashKey(key)) return null;
  const raw = window.localStorage.getItem(key);
  window.localStorage.removeItem(key);
  const payload = readPayload(raw);
  if (!payload) return null;
  if (isExpired(payload, Date.now())) return null;
  return payload.text;
}

/**
 * The analyser URL (relative — same-origin) carrying `key` as the one-shot
 * capture-handoff query flag, for `window.open`. `authKey` (optional) adds
 * the SEPARATE one-shot auth-handoff query flag (see below) alongside it —
 * omitted, the URL is byte-identical to the capture-only handoff.
 */
export function buildAnalyserHandoffUrl(key: string, authKey?: string): string {
  const params = new URLSearchParams({ [CONSOLE_HANDOFF_QUERY_PARAM]: key });
  if (authKey) params.set(CONSOLE_AUTH_HANDOFF_QUERY_PARAM, authKey);
  return `${ANALYSER_PATH}?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Auth handoff — a SECOND, independent one-shot localStorage stash carrying
// the console tab's own access token into the new analyser tab.
//
// Why this exists: the analyser tab opens via `window.open(..., 'noopener')`
// with a brand-new, unauthenticated sessionStorage (auth is per-tab — see
// TOKEN_STORAGE_KEY's doc). Without this, the new tab always hits the login
// screen and loses the capture-handoff query string/context. RequireAuth
// consumes this stash BEFORE its normal "no token → /login" redirect fires
// (see auth/RequireAuth.tsx), so a still-signed-in console user lands
// straight in the analyser with the capture intact.
//
// Same one-shot (delete-on-read) + own-namespace-only mechanics as the
// capture stash above, but:
//  - a DELIBERATELY much shorter TTL (60 s vs. 10 min) — this only needs to
//    survive the instant between the click and the new tab's first render,
//    not an abandoned-tab window, and it's carrying a bearer credential
//    rather than diagnostic text;
//  - its own key prefix/query param, so a capture-handoff link can never be
//    misread as an auth-handoff link (or vice versa) and the two stashes
//    never collide.
//
// Security posture (owner-authorized tradeoff, per the console-ux-fixes
// task): the token sits in localStorage — normally reserved for non-secret
// data — for at most the seconds between the click and the new tab
// consuming it, namespaced under its own prefix, TTL'd at 60 s, and
// deleted on the very first read whether or not it was still valid.

/** Query-string flag on the analyser route that carries the auth-handoff
 *  stash key, alongside {@link CONSOLE_HANDOFF_QUERY_PARAM}. */
export const CONSOLE_AUTH_HANDOFF_QUERY_PARAM = 'consoleAuthHandoff';

const AUTH_STORAGE_PREFIX = 'arms.sasConsoleAuthHandoff.';

/** 60 s — long enough for the click→new-tab→RequireAuth-mount round trip
 *  (well under a second in practice), short enough that a copied/leaked
 *  link is worthless within a minute. */
const AUTH_HANDOFF_TTL_MS = 60 * 1000;

type AuthStashPayload = { token: string; stashedAt: number };

function isAuthStashKey(key: string): boolean {
  return key.startsWith(AUTH_STORAGE_PREFIX);
}

function readAuthPayload(raw: string | null): AuthStashPayload | null {
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<AuthStashPayload>;
    if (typeof parsed.token !== 'string' || typeof parsed.stashedAt !== 'number') return null;
    return { token: parsed.token, stashedAt: parsed.stashedAt };
  } catch {
    return null;
  }
}

function isAuthExpired(payload: AuthStashPayload, now: number): boolean {
  return now - payload.stashedAt > AUTH_HANDOFF_TTL_MS;
}

/** Same opportunistic-sweep idea as {@link purgeExpiredStashes}, scoped to
 *  the auth-handoff namespace — run at the top of every {@link
 *  stashConsoleAuthToken} call so an abandoned click never leaves a stale
 *  bearer token sitting in localStorage past its 60 s window. */
function purgeExpiredAuthStashes(now: number): void {
  const staleKeys: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key || !isAuthStashKey(key)) continue;
    const payload = readAuthPayload(window.localStorage.getItem(key));
    if (!payload || isAuthExpired(payload, now)) staleKeys.push(key);
  }
  staleKeys.forEach((key) => window.localStorage.removeItem(key));
}

/**
 * Snapshot `token` into localStorage under a fresh one-shot key and return
 * that key — the console dialog calls this alongside {@link
 * stashConsoleCapture} and folds both keys into one {@link
 * buildAnalyserHandoffUrl} call.
 */
export function stashConsoleAuthToken(token: string): string {
  const now = Date.now();
  purgeExpiredAuthStashes(now);
  const key = `${AUTH_STORAGE_PREFIX}${uniqueSuffix()}`;
  const payload: AuthStashPayload = { token, stashedAt: now };
  window.localStorage.setItem(key, JSON.stringify(payload));
  return key;
}

/**
 * Read + immediately delete the auth-handoff stash at `key` (one-shot).
 * Returns `null` for a missing key, an already-consumed key, a malformed
 * entry, a key older than {@link AUTH_HANDOFF_TTL_MS}, or a falsy `key` —
 * exactly the same "fall back to the normal flow" contract as {@link
 * consumeConsoleCapture}. Same attacker-controlled-input guard: `key` comes
 * straight off the URL, so anything outside this module's own
 * `arms.sasConsoleAuthHandoff.*` namespace is rejected untouched.
 */
export function consumeConsoleAuthToken(key: string | null | undefined): string | null {
  if (!key || !isAuthStashKey(key)) return null;
  const raw = window.localStorage.getItem(key);
  window.localStorage.removeItem(key);
  const payload = readAuthPayload(raw);
  if (!payload) return null;
  if (isAuthExpired(payload, Date.now())) return null;
  return payload.token;
}
